/**
 * VRG-696 — the MASTER pipeline: GTM → Channels' whole-funnel view, and the slice both it and
 * the Sales Pipeline tab's flow chart are folded from.
 *
 *     Source ─▶ Entry ─▶ Furthest stage reached ─▶ Outcome
 *     (group or     Joined waitlist   Lead only / Access granted   Won / Lost / Open
 *      channel)     Added directly    / Demo / Signed up
 *
 * ── THE UNIT ─────────────────────────────────────────────────────────────────────────────
 * One DEAL. VRG-701 (Spencer, 2026-10-14): a company can have several deals (an upsell, a
 * pilot → rollout), so the unit is the deal, and the page says "Deals" (VRG-696 said "leads",
 * when one company had one deal). People at the same company count once per deal. A person with
 * no company and no deal is not here ("N people not on a deal"); the Sales Leads tab lists them.
 *
 * ── EXPANSION ────────────────────────────────────────────────────────────────────────────
 * A later deal at a company that is already a client has the source Expansion (a non-channel
 * source, `EXPANSION_SOURCE`), so it shows as its own source and never double-credits the channel
 * that brought the company in. `attribution: 'first_touch'` is the company-lifetime view: every
 * deal credited to its company's first-touch channel instead.
 *
 * ── ONE VALUE PER LEAD, SO EVERY COLUMN CONSERVES ────────────────────────────────────────
 * In Revenue mode each lead carries ONE figure through all four columns (`masterLeadValue`):
 * the Stripe actual (its PAYING subscription's list-price ARR) if one exists, at any stage
 * (VRG-700), otherwise its anticipated value (manual > calculator > form, `resolveDealValue`). Because the figure does
 * not change between columns, every column sums to the same total and in = out at every node,
 * in both modes, by construction (`master-pipeline.guard.spec.ts` asserts it). A lead with no
 * value is 0 wide and counted in `without_value`.
 *
 * ── ATTRIBUTION ──────────────────────────────────────────────────────────────────────────
 * First touch: the deal's recorded lead source (manual > company channel > People channel >
 * outreach > referral > tracked link > website journey), the same `source_channel` the Sales
 * board shows. Not recorded is a source of its own and is never redistributed.
 *
 * ── ONE SLICE, TWO CHARTS ────────────────────────────────────────────────────────────────
 * `slicePipelineDeals` applies the date range and every filter. The API folds the master view
 * AND the Sales tab's stage-by-stage flow from that same slice in one request
 * (`GET /internal/gtm/sales/flow`), so the two can never count different deals. Archived deals
 * are on no board and in no outcome: the master view leaves them out and says how many.
 */
import {clientTierLabel, isClientTier} from './client-tiers.js';
import {SOCIAL_NETWORKS, type SocialNetwork} from './social-network.js';
import {SOURCE_CHANNEL_META, SOURCE_CHANNELS, type SourceChannel} from './source-channel.js';
import {
  dealPath,
  DEAL_PLAN_LABELS,
  inDayRange,
  isDealPlan,
  LEAD_SOURCE_GROUP_LABELS,
  PIPELINE_ENTRIES,
  PIPELINE_ENTRY_LABELS,
  SALES_STAGE_LABELS,
  usdValue,
  type DayRangeLike,
  type LeadSourceGroup,
  type PipelineDeal,
  type PipelineEntry,
  type SalesStage,
} from './sales-pipeline.js';

// ── Small samples ────────────────────────────────────────────────────────────────────────

/**
 * Below this many leads a percentage is shown de-emphasised, never hidden (Spencer,
 * 2026-10-13: "slicing must always work even with tiny n"). Ten is a reading aid, not a
 * significance test: at n = 10 a single lead moves a share by 10 points.
 */
export const SMALL_SAMPLE_N = 10;
export const isSmallSample = (n: number): boolean => n < SMALL_SAMPLE_N;

// ── Columns ──────────────────────────────────────────────────────────────────────────────

export const MASTER_STAGES = ['lead', 'access_granted', 'demo', 'account_created'] as const;
export type MasterStage = (typeof MASTER_STAGES)[number];
/**
 * VRG-727 — read from `SALES_STAGE_LABELS` (the one stage-name map), so the Channels page's
 * Sankey nodes can never name a stage differently from the Sales board. Only "Lead only" is
 * its own: a deal that never left Lead, which is a column here and not a stage.
 */
export const MASTER_STAGE_LABELS: Readonly<Record<MasterStage, string>> = {
  lead: 'Lead only',
  access_granted: SALES_STAGE_LABELS.access_granted,
  demo: SALES_STAGE_LABELS.demo,
  account_created: SALES_STAGE_LABELS.account_created,
};

export const MASTER_OUTCOMES = ['won', 'open', 'lost'] as const;
export type MasterOutcome = (typeof MASTER_OUTCOMES)[number];
export const MASTER_OUTCOME_LABELS: Readonly<Record<MasterOutcome, string>> = {won: 'Won', open: 'Open', lost: 'Lost'};

/** VRG-701 adds `sub_source`: each channel split by its sub-source (Source → Sub-source, finer). */
export type MasterGranularity = 'group' | 'channel' | 'sub_source';

/**
 * Source keys at channel granularity that are not one of the 19 channels. VRG-701 adds
 * Expansion: an existing client's later deal, a source of its own and never a channel.
 */
export const MASTER_UNATTRIBUTED_SOURCES = ['expansion', 'website_unknown', 'not_recorded'] as const;
export type MasterUnattributedSource = (typeof MASTER_UNATTRIBUTED_SOURCES)[number];
export const MASTER_UNATTRIBUTED_LABELS: Readonly<Record<MasterUnattributedSource, string>> = {
  expansion: 'Expansion (existing client)',
  website_unknown: 'Website, referrer unknown',
  not_recorded: 'Not recorded',
};
export type MasterChannelKey = SourceChannel | MasterUnattributedSource;

type DealLike = Pick<
  PipelineDeal,
  'stage' | 'status' | 'closed_reason' | 'reached' | 'value' | 'committed_arr' | 'source_channel' | 'source_group' | 'opened_at'
> &
  Partial<
    Pick<
      PipelineDeal,
      | 'entry'
      | 'industry'
      | 'plan'
      | 'country'
      | 'city'
      | 'paying_arr'
      | 'first_touch_channel'
      | 'first_touch_group'
      | 'is_first_deal'
      | 'sub_source'
      | 'first_touch_sub_source'
      | 'tier'
      | 'tag_margin'
    >
  > & {
    /** Set by `applyAttribution` on a deal whose own source was Expansion, so the tile still counts it. */
    was_expansion?: boolean;
  };

export const isArchived = (d: Pick<PipelineDeal, 'status' | 'closed_reason'>): boolean =>
  d.status === 'closed' && d.closed_reason === 'archived';

/** Won / Lost / Open. A won deal later closed as lost is Lost (it left). Archived: not asked. */
export const masterOutcome = (d: Pick<PipelineDeal, 'status' | 'closed_reason' | 'stage'>): MasterOutcome =>
  d.status === 'closed' ? 'lost' : d.stage === 'paid_client' ? 'won' : 'open';

/**
 * The furthest of Access granted / Demo / Signed up the deal passed through (the same
 * walk as the Sales flow chart, `dealPath`), or Lead only. A Won deal sits under the furthest
 * of those it reached on the way.
 */
export const masterStage = (d: Pick<PipelineDeal, 'stage' | 'reached'>): MasterStage => {
  const path = new Set<SalesStage>(dealPath(d));
  if (path.has('account_created')) return 'account_created';
  if (path.has('demo')) return 'demo';
  if (path.has('access_granted')) return 'access_granted';
  return 'lead';
};

export const masterEntry = (d: Partial<Pick<PipelineDeal, 'entry'>>): PipelineEntry => (d.entry === 'waitlist' ? 'waitlist' : 'direct');

export const masterSourceKey = (
  d: Pick<PipelineDeal, 'source_channel' | 'source_group'> & Partial<Pick<PipelineDeal, 'sub_source'>>,
  g: MasterGranularity,
): string => {
  if (g === 'group') return d.source_group;
  // VRG-701 — "channel › sub-source"; a deal with no sub-source stays on its channel's own node.
  if (g === 'sub_source') return d.sub_source ? `${masterChannelKey(d)}|${d.sub_source.key}` : masterChannelKey(d);
  return masterChannelKey(d);
};

export const masterChannelKey = (d: Pick<PipelineDeal, 'source_channel' | 'source_group'>): MasterChannelKey =>
  d.source_channel ?? (d.source_group === 'expansion' ? 'expansion' : d.source_group === 'website' ? 'website_unknown' : 'not_recorded');

/** VRG-701 — an existing client's later deal (the Expansion source). */
export const isExpansionDeal = (d: Pick<PipelineDeal, 'source_channel' | 'source_group'>): boolean =>
  !d.source_channel && d.source_group === 'expansion';

// ── Attribution (VRG-701) ────────────────────────────────────────────────────────────────

/**
 * `deal` (the default): each deal under its OWN source, Expansion included. `first_touch`: the
 * company-lifetime view, every deal credited to its company's first-touch channel, so "what did
 * Trade shows bring" includes the upsells of the companies it brought.
 */
export const MASTER_ATTRIBUTIONS = ['deal', 'first_touch'] as const;
export type MasterAttribution = (typeof MASTER_ATTRIBUTIONS)[number];
export const parseMasterAttribution = (v: unknown): MasterAttribution => (v === 'first_touch' ? 'first_touch' : 'deal');

/** The deals with their source swapped for the company's first touch, when asked. */
export const applyAttribution = <D extends DealLike>(deals: readonly D[], mode: MasterAttribution | null | undefined): D[] =>
  mode === 'first_touch'
    ? deals.map(d =>
        d.first_touch_group
          ? {
              ...d,
              was_expansion: isExpansionDeal(d),
              source_channel: d.first_touch_channel ?? null,
              source_group: d.first_touch_group,
              sub_source: d.first_touch_sub_source ?? null,
            }
          : d,
      )
    : [...deals];

export const masterSourceLabel = (key: string, g: MasterGranularity, subLabel?: string | null): string => {
  if (g === 'group') return LEAD_SOURCE_GROUP_LABELS[key as LeadSourceGroup] ?? key;
  if (g === 'sub_source' && key.includes('|')) return `${masterSourceLabel(key.split('|')[0], 'channel')} › ${subLabel ?? key.split('|').slice(1).join('|')}`;
  if ((MASTER_UNATTRIBUTED_SOURCES as readonly string[]).includes(key)) return MASTER_UNATTRIBUTED_LABELS[key as MasterUnattributedSource];
  return SOURCE_CHANNEL_META[key as SourceChannel]?.label ?? key;
};

// ── The value rule ───────────────────────────────────────────────────────────────────────

export type MasterValueKind = 'actual' | 'predicted';

/**
 * The ONE figure a lead carries through every column, annual USD (VRG-700, Spencer 2026-10-13:
 * "actual if a paying subscription exists, regardless of stage; else anticipated"):
 *   - the company has a PAYING-NOW subscription (`payingVerdict`, VRG-727: live mode, active,
 *     not trialing, and a live paid invoice above $0 in 60 days) → its ARR (Stripe: list price, or
 *     the latest invoice annualised), `actual` — at ANY stage and outcome;
 *   - otherwise → the anticipated value (manual > calculator > form) in USD, `predicted`;
 *   - neither → null: the lead is 0 wide and counted as without a value.
 * So a Won deal with NO paying subscription (a one-off invoice, a cancelled plan, a $0 pilot, a
 * test-mode subscription) stays PREDICTED (VRG-696 decision #1) and shows in the tooltip's
 * actual/predicted split rather than as a silent 0. A $0 plan is no longer "an actual 0": it is
 * not paying, so the lead carries its anticipated value.
 */
export const masterLeadValue = (
  d: Pick<PipelineDeal, 'value'> & Partial<Pick<PipelineDeal, 'paying_arr'>>,
): {arr: number | null; kind: MasterValueKind | null} => {
  if (typeof d.paying_arr === 'number' && Number.isFinite(d.paying_arr) && d.paying_arr > 0) {
    return {arr: d.paying_arr, kind: 'actual'};
  }
  const v = usdValue(d.value);
  return v === null ? {arr: null, kind: null} : {arr: v, kind: 'predicted'};
};

// ── Filters ──────────────────────────────────────────────────────────────────────────────

/** The facet value for a field that is not set, so "not set" can be sliced like any other. */
export const FACET_NOT_SET = '__none';

export interface MasterPipelineFilters {
  /** Cohort: deals that ENTERED the pipeline in the range (`opened_at`). Absent = all time. */
  range?: DayRangeLike | null;
  /** Each list is OR within the facet, AND across facets. Empty / absent = no filter. */
  industry?: readonly string[] | null;
  /** VRG-701 — the deal's plan (replaces VRG-696's `vera_product`). */
  plan?: readonly string[] | null;
  /** VRG-701 — the company's client tier ('tier_1'…). */
  tier?: readonly string[] | null;
  /** VRG-701 — sub-source keys (`kind:id`), from the facet options. */
  sub_source?: readonly string[] | null;
  country?: readonly string[] | null;
  city?: readonly string[] | null;
  /**
   * Anticipated value, ANNUAL USD, inclusive. A bound set leaves out deals with no USD value.
   * VRG-700: always annual on the wire and in the URL, whatever the page's MRR/ARR switch shows
   * (the inputs convert, `periodBoundFromInput`), so a shared link means the same slice.
   */
  value_min?: number | null;
  value_max?: number | null;
  /**
   * Stripe actual: the PAYING subscription's list-price ARR (VRG-700, `paying_arr`), ANNUAL USD,
   * inclusive. A bound set leaves out deals with none (a $0 or test-mode plan included).
   */
  actual_min?: number | null;
  actual_max?: number | null;
  /** VRG-701 — whose source a deal is counted under. Absent = `deal`. */
  attribution?: MasterAttribution | null;
}

export const MASTER_FACETS = ['industry', 'plan', 'tier', 'country', 'city', 'sub_source'] as const;
export type MasterFacet = (typeof MASTER_FACETS)[number];

/** A deal's value for a facet, normalised the way the filter compares it. */
export const facetValue = (d: Partial<Pick<PipelineDeal, Exclude<MasterFacet, 'sub_source'>>> & Partial<Pick<PipelineDeal, 'sub_source'>>, f: MasterFacet): string => {
  if (f === 'sub_source') {
    const s = d.sub_source as unknown;
    // A deal's object, or a filter value (already the key).
    if (typeof s === 'string') return s.trim() || FACET_NOT_SET;
    return s && typeof s === 'object' && (s as {key?: string}).key ? (s as {key: string}).key : FACET_NOT_SET;
  }
  const raw = d[f];
  if (typeof raw !== 'string' || !raw.trim()) return FACET_NOT_SET;
  const v = raw.trim();
  if (f === 'country') return v.toUpperCase();
  // Cities are typed by hand: "chicago" and "Chicago " are one city.
  if (f === 'city') return v.toLowerCase();
  return v;
};

const inBounds = (v: number, min: number | null | undefined, max: number | null | undefined): boolean =>
  (min === null || min === undefined || v >= min) && (max === null || max === undefined || v <= max);

const hasBound = (min: number | null | undefined, max: number | null | undefined): boolean =>
  (min !== null && min !== undefined) || (max !== null && max !== undefined);

/** Does one deal pass every filter? (Archived deals are not excluded here: see the header.) */
export const passesMasterFilters = (d: DealLike, f: MasterPipelineFilters): boolean => {
  if (f.range && (f.range.since || f.range.until) && !inDayRange(d.opened_at, f.range)) return false;
  for (const facet of MASTER_FACETS) {
    const want = f[facet];
    if (!want || want.length === 0) continue;
    const norm = new Set(want.map(w => facetValue({[facet]: w} as never, facet)));
    // `FACET_NOT_SET` normalises to itself; a literal "__none" in a list is the not-set bucket.
    if (want.includes(FACET_NOT_SET)) norm.add(FACET_NOT_SET);
    if (!norm.has(facetValue(d, facet))) return false;
  }
  if (hasBound(f.value_min, f.value_max)) {
    const v = usdValue(d.value);
    if (v === null || !inBounds(v, f.value_min, f.value_max)) return false;
  }
  if (hasBound(f.actual_min, f.actual_max)) {
    const a = d.paying_arr;
    if (typeof a !== 'number' || !Number.isFinite(a) || !inBounds(a, f.actual_min, f.actual_max)) return false;
  }
  return true;
};

/** The shared slice: every deal in the range that passes the filters, archived included. */
export const slicePipelineDeals = <D extends DealLike>(deals: readonly D[], f: MasterPipelineFilters): D[] =>
  deals.filter(d => passesMasterFilters(d, f));

// ── The fold ─────────────────────────────────────────────────────────────────────────────

/** What every node and band carries: n, and the revenue split. All money annual USD. */
export interface MasterMeasure {
  n: number;
  /** Σ the one value per lead (actual + predicted). */
  arr: number;
  actual_arr: number;
  predicted_arr: number;
  /** Leads with no value (0 wide in Revenue mode). */
  without_value: number;
}

export type MasterColumn = 'source' | 'entry' | 'stage' | 'outcome';

export interface MasterNode extends MasterMeasure {
  id: string;
  key: string;
  label: string;
  column: MasterColumn;
}

export interface MasterLink extends MasterMeasure {
  source: string;
  target: string;
}

export interface MasterFlow {
  granularity: MasterGranularity;
  nodes: MasterNode[];
  links: MasterLink[];
}

/** The Channels table's row for one source channel (or an unattributed bucket). */
export interface MasterChannelRow {
  key: MasterChannelKey;
  /** Null for the non-channel buckets (Expansion, Website referrer unknown, Not recorded). */
  channel: SourceChannel | null;
  /** VRG-701 — deals (was `leads`: the unit is the deal). */
  deals: number;
  /** deals ÷ all deals in the slice, 0–1. */
  share: number;
  /**
   * Open deals and Σ their one value ("Pipeline value"): the anticipated value, or the Stripe
   * actual for an open deal that is paying (rare: one a person moved back out of Won). Open
   * deals with neither are counted in `pipeline_without_value`.
   */
  pipeline_n: number;
  pipeline_arr: number;
  pipeline_without_value: number;
  /** VRG-736 — Σ tag margin a year of the same open deals: shown as "+ tags", never in `pipeline_arr`. */
  pipeline_tag_margin: number;
  /** Won deals, Σ their paying subscriptions' ARR ("Won revenue"), and Won deals with nothing paying. */
  won_n: number;
  won_arr: number;
  won_without_revenue: number;
  lost_n: number;
  /** Won ÷ (Won + Lost); null with no closed deal. n = `closed_n`. */
  closed_n: number;
  win_rate: number | null;
}

/**
 * VRG-735 — the Social channel's deals split by network (its `social_network` sub-source), for
 * the Channels table's LinkedIn / Instagram / Other social sub-rows. `not_split` = Social deals
 * with no network (set to Social by hand, with no referrer or utm to read one from). Same
 * figures as `MasterChannelRow`; `share` is of ALL deals in the slice, like the channel rows.
 */
export const MASTER_SOCIAL_SPLIT_KEYS = [...SOCIAL_NETWORKS, 'not_split'] as const;
export type MasterSocialSplitKey = (typeof MASTER_SOCIAL_SPLIT_KEYS)[number];
export interface MasterSocialNetworkRow extends Omit<MasterChannelRow, 'key' | 'channel'> {
  key: MasterSocialSplitKey;
}

/** A deal's network under Social, from its sub-source key (`social_network:<network>`). */
export const masterSocialSplitKey = (d: Partial<Pick<PipelineDeal, 'sub_source'>>): MasterSocialSplitKey => {
  const k = d.sub_source?.key ?? '';
  const id = k.startsWith('social_network:') ? k.slice('social_network:'.length) : '';
  return (SOCIAL_NETWORKS as readonly string[]).includes(id) ? (id as SocialNetwork) : 'not_split';
};

export interface MasterPipeline {
  total: MasterMeasure;
  /** VRG-701 — of `total.n`: deals under the Expansion source, and the rest (new business). */
  expansion_n: number;
  new_n: number;
  /** Archived deals in the slice: on no board, in no outcome, left out of every figure here. */
  archived_excluded: number;
  by_group: MasterFlow;
  by_channel: MasterFlow;
  /** VRG-701 — the channels split by sub-source. */
  by_sub_source: MasterFlow;
  channels: MasterChannelRow[];
  /** VRG-735 — the Social row's deals by network (only keys that have deals). */
  social_networks: MasterSocialNetworkRow[];
}

const zero = (): MasterMeasure => ({n: 0, arr: 0, actual_arr: 0, predicted_arr: 0, without_value: 0});

const addTo = (m: MasterMeasure, v: {arr: number | null; kind: MasterValueKind | null}) => {
  m.n += 1;
  if (v.arr === null) {
    m.without_value += 1;
    return;
  }
  m.arr += v.arr;
  if (v.kind === 'actual') m.actual_arr += v.arr;
  else m.predicted_arr += v.arr;
};

const foldFlow = (deals: readonly DealLike[], g: MasterGranularity): MasterFlow => {
  const nodes = new Map<string, MasterNode>();
  const links = new Map<string, MasterLink>();
  const node = (column: MasterColumn, key: string, label: string): MasterNode => {
    const id = `${column}:${key}`;
    let n = nodes.get(id);
    if (!n) {
      n = {id, key, label, column, ...zero()};
      nodes.set(id, n);
    }
    return n;
  };
  const link = (a: MasterNode, b: MasterNode): MasterLink => {
    const id = `${a.id}>${b.id}`;
    let l = links.get(id);
    if (!l) {
      l = {source: a.id, target: b.id, ...zero()};
      links.set(id, l);
    }
    return l;
  };
  for (const d of deals) {
    const v = masterLeadValue(d);
    const srcKey = masterSourceKey(d, g);
    const entry = masterEntry(d);
    const stage = masterStage(d);
    const outcome = masterOutcome(d);
    const path = [
      node('source', srcKey, masterSourceLabel(srcKey, g, d.sub_source?.label)),
      node('entry', entry, PIPELINE_ENTRY_LABELS[entry]),
      node('stage', stage, MASTER_STAGE_LABELS[stage]),
      node('outcome', outcome, MASTER_OUTCOME_LABELS[outcome]),
    ];
    for (const n of path) addTo(n, v);
    for (let i = 1; i < path.length; i += 1) addTo(link(path[i - 1], path[i]), v);
  }
  // Stable order: sources by size (unattributed last), the other columns in their own order.
  const sourceRank = (n: MasterNode): number =>
    n.key === 'not_recorded' ? 3 : n.key === 'expansion' ? 2 : n.key === 'website_unknown' || n.key === 'other' ? 1 : 0;
  const order = <T extends string>(keys: readonly T[]) => (a: MasterNode, b: MasterNode) => keys.indexOf(a.key as T) - keys.indexOf(b.key as T);
  const all = [...nodes.values()];
  const sources = all
    .filter(n => n.column === 'source')
    .sort((a, b) => sourceRank(a) - sourceRank(b) || b.n - a.n || a.label.localeCompare(b.label));
  return {
    granularity: g,
    nodes: [
      ...sources,
      ...all.filter(n => n.column === 'entry').sort(order(PIPELINE_ENTRIES)),
      ...all.filter(n => n.column === 'stage').sort(order(MASTER_STAGES)),
      ...all.filter(n => n.column === 'outcome').sort(order(MASTER_OUTCOMES)),
    ],
    links: [...links.values()],
  };
};

/** Deals → one row per key, with the Channels table's figures (shared by channels and social networks). */
const foldRowsBy = <K extends string, R extends Omit<MasterChannelRow, 'key' | 'channel'> & {key: K}>(
  deals: readonly DealLike[],
  total: number,
  keyOf: (d: DealLike) => K,
  extra: (d: DealLike) => Omit<R, keyof MasterSocialNetworkRow>,
  order: readonly string[],
): R[] => {
  const rows = new Map<K, R>();
  for (const d of deals) {
    const key = keyOf(d);
    let r = rows.get(key);
    if (!r) {
      r = {
        ...extra(d),
        key,
        deals: 0,
        share: 0,
        pipeline_n: 0,
        pipeline_arr: 0,
        pipeline_without_value: 0,
        pipeline_tag_margin: 0,
        won_n: 0,
        won_arr: 0,
        won_without_revenue: 0,
        lost_n: 0,
        closed_n: 0,
        win_rate: null,
      } as R;
      rows.set(key, r);
    }
    r.deals += 1;
    const outcome = masterOutcome(d);
    if (outcome === 'open') {
      r.pipeline_n += 1;
      // The lead's ONE value (VRG-700), so Σ pipeline value = the Open node in Revenue mode.
      const v = masterLeadValue(d).arr;
      if (v === null) r.pipeline_without_value += 1;
      else r.pipeline_arr += v;
      if (typeof d.tag_margin === 'number' && Number.isFinite(d.tag_margin)) r.pipeline_tag_margin += d.tag_margin;
    } else if (outcome === 'won') {
      r.won_n += 1;
      const lv = masterLeadValue(d);
      if (lv.kind === 'actual' && lv.arr !== null) r.won_arr += lv.arr;
      else r.won_without_revenue += 1;
    } else {
      r.lost_n += 1;
    }
  }
  return [...rows.values()]
    .map(r => {
      r.share = total > 0 ? r.deals / total : 0;
      r.closed_n = r.won_n + r.lost_n;
      r.win_rate = r.closed_n > 0 ? r.won_n / r.closed_n : null;
      return r;
    })
    .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
};

const foldChannels = (deals: readonly DealLike[], total: number): MasterChannelRow[] =>
  foldRowsBy<MasterChannelKey, MasterChannelRow>(deals, total, masterChannelKey, d => ({channel: d.source_channel ?? null}), [
    ...SOURCE_CHANNELS,
    ...MASTER_UNATTRIBUTED_SOURCES,
  ]);

/** VRG-735 — the Social channel's deals by network. */
const foldSocialNetworks = (deals: readonly DealLike[], total: number): MasterSocialNetworkRow[] =>
  foldRowsBy<MasterSocialSplitKey, MasterSocialNetworkRow>(
    deals.filter(d => masterChannelKey(d) === 'social_display_ads'),
    total,
    masterSocialSplitKey,
    () => ({}),
    MASTER_SOCIAL_SPLIT_KEYS,
  );

/**
 * The master view of a SLICE (`slicePipelineDeals`): both granularities, the channel rows and
 * the totals. Archived deals in the slice are counted and left out of everything else.
 */
export const foldMasterPipeline = (slice: readonly DealLike[]): MasterPipeline => {
  const live = slice.filter(d => !isArchived(d));
  const total = zero();
  for (const d of live) addTo(total, masterLeadValue(d));
  // An Expansion deal is still one in the company-lifetime view (its source is swapped, not its nature).
  const expansion = live.filter(d => d.was_expansion ?? isExpansionDeal(d)).length;
  return {
    total,
    expansion_n: expansion,
    new_n: live.length - expansion,
    archived_excluded: slice.length - live.length,
    by_group: foldFlow(live, 'group'),
    by_channel: foldFlow(live, 'channel'),
    by_sub_source: foldFlow(live, 'sub_source'),
    channels: foldChannels(live, live.length),
    social_networks: foldSocialNetworks(live, live.length),
  };
};

// ── Filter options ───────────────────────────────────────────────────────────────────────

export interface MasterFacetOption {
  value: string;
  /** Display label: the stored spelling (first seen), or "Not set". */
  label: string;
  n: number;
}

/**
 * The options each facet offers, counted over the RANGE cohort with no other filter applied,
 * so choosing one industry does not make the others disappear from the picker. Not set is an
 * option whenever any deal lacks the field.
 */
export const masterFacetOptions = (
  deals: readonly DealLike[],
  range: DayRangeLike | null | undefined,
  labelFor: Partial<Record<MasterFacet, (value: string) => string>> = {},
): Record<MasterFacet, MasterFacetOption[]> => {
  const cohort = deals.filter(d => !isArchived(d) && (!range || (!range.since && !range.until) || inDayRange(d.opened_at, range)));
  const out = {} as Record<MasterFacet, MasterFacetOption[]>;
  for (const f of MASTER_FACETS) {
    const by = new Map<string, MasterFacetOption>();
    for (const d of cohort) {
      const v = facetValue(d, f);
      const o = by.get(v) ?? {
        value: v === FACET_NOT_SET ? FACET_NOT_SET : f === 'city' ? String(d.city).trim() : v,
        label:
          v === FACET_NOT_SET
            ? 'Not set'
            : f === 'sub_source' && d.sub_source
              ? `${masterSourceLabel(masterChannelKey(d), 'channel')} › ${d.sub_source.label}`
              : (labelFor[f]?.(v) ??
                (f === 'city'
                  ? String(d.city).trim()
                  : f === 'plan' && isDealPlan(v)
                    ? DEAL_PLAN_LABELS[v]
                    : f === 'tier' && isClientTier(v)
                      ? clientTierLabel(v)
                      : v)),
        n: 0,
      };
      o.n += 1;
      by.set(v, o);
    }
    out[f] = [...by.values()].sort((a, b) => (a.value === FACET_NOT_SET ? 1 : b.value === FACET_NOT_SET ? -1 : b.n - a.n || a.label.localeCompare(b.label)));
  }
  return out;
};

// ── The MRR/ARR switch and the money filters (VRG-700) ───────────────────────────────────

export type RevenuePeriod = 'mrr' | 'arr';

/**
 * The switch's default on GTM → Channels and GTM → Sales: ARR (Spencer, 2026-10-13). Only a
 * non-default choice is written to the URL (`?period=mrr`), so an old link with no `period`
 * opens in ARR.
 */
export const DEFAULT_REVENUE_PERIOD: RevenuePeriod = 'arr';

export const parseRevenuePeriod = (v: unknown): RevenuePeriod => (v === 'mrr' ? 'mrr' : v === 'arr' ? 'arr' : DEFAULT_REVENUE_PERIOD);

/**
 * A money bound (annual USD, as stored in the URL and sent to the API) shown in the switch's
 * period. MRR = ARR ÷ 12, kept to the cent so ARR → MRR → ARR is stable for whole-dollar MRR.
 */
export const periodBoundForInput = (annual: number | null, period: RevenuePeriod): number | null =>
  annual === null ? null : period === 'mrr' ? Math.round((annual / 12) * 100) / 100 : annual;

/**
 * A bound typed in the switch's period → annual USD for the URL. The URL is ALWAYS annual
 * (documented on `MasterPipelineFilters`), so changing the switch never changes the slice.
 */
export const periodBoundFromInput = (typed: number | null, period: RevenuePeriod): number | null =>
  typed === null ? null : period === 'mrr' ? Math.round(typed * 12 * 100) / 100 : typed;

// ── The value range controls (VRG-701) ───────────────────────────────────────────────────

/**
 * VRG-701 (Spencer, 2026-10-14) — the snap points of the "Deal value" and "Stripe revenue" range
 * sliders, ANNUAL USD. Fixed, never derived from the data, so a URL means the same slice next
 * month. Roughly log-spaced (a deal is $1K to $35M+ ARR: a linear track would put every deal in
 * its first pixel). The LAST stop means "no upper limit", the first "no lower limit" — "Any".
 */
export const VALUE_RANGE_STOPS = [0, 1_000, 5_000, 10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000, 5_000_000, 10_000_000, 35_000_000] as const;
export const VALUE_RANGE_MAX_INDEX = VALUE_RANGE_STOPS.length - 1;

/** A bound (annual USD or null = unbounded) → the nearest stop index, as the slider shows it. */
export const valueStopIndex = (annual: number | null, end: 'min' | 'max'): number => {
  if (annual === null) return end === 'min' ? 0 : VALUE_RANGE_MAX_INDEX;
  let best = 0;
  for (let i = 0; i < VALUE_RANGE_STOPS.length; i += 1) {
    if (Math.abs(VALUE_RANGE_STOPS[i] - annual) < Math.abs(VALUE_RANGE_STOPS[best] - annual)) best = i;
  }
  return best;
};

/** A stop index → the bound for the URL: null at the open ends ("Any"). */
export const valueStopBound = (index: number, end: 'min' | 'max'): number | null => {
  const i = Math.max(0, Math.min(VALUE_RANGE_MAX_INDEX, Math.round(index)));
  if (end === 'min' && i === 0) return null;
  if (end === 'max' && i === VALUE_RANGE_MAX_INDEX) return null;
  return VALUE_RANGE_STOPS[i];
};
